import React, { useState, useEffect } from 'react';
import { HelpCircle, ChevronRight, ChevronLeft, Send, CheckCircle, Loader2 } from 'lucide-react';
import apiClient from '../../../lib/apiClient';

const Questionnaire = ({ departmentId, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const data = await apiClient.get('/api/zero-trust/questions');
        setQuestions(Array.isArray(data) ? data : data.questions || []);
      } catch (err) {
        setError('Failed to load questions');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const currentQuestion = questions[currentStep];
  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!departmentId) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(`/api/zero-trust/departments/${departmentId}/questionnaire`, {
        answers
      });
      setSubmitted(true);
      onComplete?.();
    } catch (err) {
      setError('Failed to submit questionnaire. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="questionnaire questionnaire--loading">
        <Loader2 size={24} className="spin" />
        <p>Loading questionnaire...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="questionnaire questionnaire--success">
        <CheckCircle size={40} style={{ color: 'var(--zt-strong)' }} />
        <h3>Questionnaire Complete</h3>
        <p>Your responses have been saved and will be used to generate maturity assessments.</p>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="questionnaire questionnaire--empty">
        <HelpCircle size={32} style={{ color: 'var(--color-text-muted)' }} />
        <p>No questionnaire questions available</p>
      </div>
    );
  }

  return (
    <div className="questionnaire">
      {/* Progress bar */}
      <div className="questionnaire-progress">
        <div className="questionnaire-progress-bar">
          <div
            className="questionnaire-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="questionnaire-progress-label">
          {currentStep + 1} / {questions.length}
        </span>
      </div>

      {/* Question */}
      {currentQuestion && (
        <div className="questionnaire-question">
          <div className="question-category">
            {currentQuestion.pillar_name || currentQuestion.category || 'Assessment'}
          </div>
          <h3 className="question-text">{currentQuestion.question_text || currentQuestion.question}</h3>

          {currentQuestion.question_description && (
            <p className="question-desc">{currentQuestion.question_description}</p>
          )}

          {/* Answer options */}
          <div className="question-options">
            {(currentQuestion.options || currentQuestion.answers || []).map((option, idx) => {
              const optionValue = option.value || option.option_value || option;
              const optionLabel = option.label || option.option_label || option;
              const isSelected = answers[currentQuestion.question_id] === optionValue;

              return (
                <button
                  key={idx}
                  className={`question-option ${isSelected ? 'question-option--selected' : ''}`}
                  onClick={() => handleAnswer(currentQuestion.question_id, optionValue)}
                  aria-pressed={isSelected}
                >
                  <div className="question-option-indicator" />
                  <span>{optionLabel}</span>
                </button>
              );
            })}

            {/* Fallback: text input if no options */}
            {(!currentQuestion.options || !currentQuestion.options.length) &&
             (!currentQuestion.answers || !currentQuestion.answers.length) && (
              <textarea
                className="question-text-input"
                placeholder="Enter your response..."
                value={answers[currentQuestion.question_id] || ''}
                onChange={e => handleAnswer(currentQuestion.question_id, e.target.value)}
                rows={4}
              />
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="questionnaire-error">{error}</div>
      )}

      {/* Navigation */}
      <div className="questionnaire-nav">
        <button
          className="btn-secondary"
          onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
          disabled={currentStep === 0}
        >
          <ChevronLeft size={16} />
          Previous
        </button>

        {currentStep < questions.length - 1 ? (
          <button
            className="btn-primary"
            onClick={() => setCurrentStep(prev => Math.min(questions.length - 1, prev + 1))}
          >
            Next
            <ChevronRight size={16} />
          </button>
        ) : (
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !departmentId}
          >
            {submitting ? (
              <><Loader2 size={16} className="spin" /> Submitting...</>
            ) : (
              <><Send size={16} /> Submit</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default Questionnaire;
